import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { isSysadminRole } from "../helpers/roleUtils";
import { sessionData } from "../helpers/session-data";
import type { RootState } from "../types/store";
import { categoryService } from "../services/category.service";
import type { CategoryTreeItem } from "../services/categories.service";
import { selectUserRole } from "./authSelectors";

const { getAllCategoryInfo } = categoryService;

export interface CategoriesState {
  categoryTree: CategoryTreeItem[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
}

const initialState: CategoriesState = {
  categoryTree: [],
  isLoading: false,
  isRefreshing: false,
  error: null,
};

export const fetchCategoryTree = createAsyncThunk(
  "categories/fetchCategoryTree",
  async (options: { isRefresh?: boolean } | void, { rejectWithValue, getState }) => {
    const { business_id, device_token, session_id } = sessionData();
    if (!device_token || !session_id) {
      return rejectWithValue("Session not available");
    }
    const role = selectUserRole(getState() as RootState);
    const usePlatformTree = isSysadminRole(role);
    const res = await getAllCategoryInfo({
      business_id: usePlatformTree ? null : business_id,
      device_token,
      session_id,
      category_type: "all",
    });
    if (!res.success) {
      return rejectWithValue(res.errors ?? "Failed to load categories");
    }
    const categoryTree = Array.isArray(res.data?.category_tree) ? res.data.category_tree : [];
    return { categoryTree };
  }
);

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    setCategoryTree: (state, action: PayloadAction<CategoryTreeItem[]>) => {
      state.categoryTree = action.payload;
      state.error = null;
    },
    clearCategoriesError: (state) => {
      state.error = null;
    },
    clearCategoryTree: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategoryTree.pending, (state, action) => {
        const arg = action.meta.arg;
        const isRefresh = arg && typeof arg === "object" && arg.isRefresh === true;
        if (isRefresh) {
          state.isRefreshing = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchCategoryTree.fulfilled, (state, action) => {
        state.categoryTree = action.payload.categoryTree;
        state.isLoading = false;
        state.isRefreshing = false;
        state.error = null;
      })
      .addCase(fetchCategoryTree.rejected, (state, action) => {
        state.isLoading = false;
        state.isRefreshing = false;
        state.error =
          (action.payload as string) ?? action.error.message ?? "Failed to load categories";
      });
  },
});

export const { setCategoryTree, clearCategoriesError, clearCategoryTree } = categoriesSlice.actions;

export const selectCategoryTree = (state: { categories: CategoriesState }): CategoryTreeItem[] =>
  state.categories?.categoryTree ?? [];

export const selectCategoriesLoading = (state: { categories: CategoriesState }) =>
  state.categories?.isLoading ?? false;

export const selectCategoriesRefreshing = (state: { categories: CategoriesState }) =>
  state.categories?.isRefreshing ?? false;

export const selectCategoriesError = (state: { categories: CategoriesState }) =>
  state.categories?.error ?? null;

export const selectCategoriesData = (state: { categories: CategoriesState }) => ({
  categoryTree: state.categories?.categoryTree ?? [],
  isLoading: state.categories?.isLoading ?? false,
  isRefreshing: state.categories?.isRefreshing ?? false,
  error: state.categories?.error ?? null,
});

export default categoriesSlice.reducer;
